import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { ReportProblem, VisibilityOutlined, Forum } from "@mui/icons-material";
import { toast } from "react-toastify";
import { yellow } from "@mui/material/colors";
import {
  Avatar,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
  makeStyles,
  Chip,
  Grid,
  Tooltip,
  Box,
} from "@material-ui/core";
import { format, isSameDay, parseISO } from "date-fns";
import { grey } from "@material-ui/core/colors";
import { getBackendUrl } from "../../config";
import UserStatusIcon from "../UserModal/statusIcon";
import UserCardHeader from "./UserCardHeader";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    justifyContent: "space-between",
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    gap: theme.spacing(2),
    justifyContent: "center", // Centraliza horizontalmente
    alignItems: "center", // Centraliza verticalmente as listas
    minHeight: '100vh', // Garante centralização vertical mesmo com poucas listas
    padding: theme.spacing(2),
    width: '100%',
  },
  card: {
    width: "410px", // Reduzido para melhor aproveitamento em telas HD
    margin: theme.spacing(2, 0),
    borderRadius: 18,
    background: theme.palette.background.paper,
    boxShadow: "0 2px 16px 0 rgba(60,72,88,0.08)",
    padding: theme.spacing(0),
    border: "none",
    transition: "box-shadow 0.18s cubic-bezier(.4,0,.2,1), background 0.18s cubic-bezier(.4,0,.2,1)",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: 0,
    },
    overflow: 'hidden', // Garante que borderRadius funcione corretamente
  },
  cardHeader: {
    width: "100%",
    minHeight: 70,
    background: theme.palette.background.paper,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: "none",
    marginBottom: 0,
    padding: theme.spacing(2, 2, 1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    transition: 'background 0.2s',
  },
  cardHeaderPending: {
    width: "100%",
    minHeight: 70,
    background: theme.palette.background.paper,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: "none",
    marginBottom: 0,
    padding: theme.spacing(2, 2, 1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  changeWarap: {
    width: "100%",
    padding: theme.spacing(0, 1),
    margin: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.6em",
  },
  lastMessageTime: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    marginRight: "1px",
    color: grey[400],
    fontSize: "0.95em",
    minWidth: 60,
  },
  lastMessageTimeUnread: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    color: "#43a047",
    fontWeight: "bold",
    marginRight: "1px",
    fontSize: "1em",
    minWidth: 60,
  },
  pending: {
    color: "#fbc02d",
    fontSize: "24px",
    marginLeft: 4,
  },
  ticketBox: {
    transition: 'background 0.2s, box-shadow 0.2s',
    borderRadius: 18,
    cursor: 'pointer',
    background: theme.palette.background.paper,
    boxShadow: 'none',
    '&:hover': {
      background: theme.palette.action.hover,
      boxShadow: theme.shadows[2],
    },
  },
  ticketBoxDark: {
    background: theme.palette.background.default,
    '&:hover': {
      background: theme.palette.action.selected,
      boxShadow: theme.shadows[4],
    },
  },
}));

const DashboardManage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;
  const isDark = theme.palette.type === "dark" || theme.palette.mode === "dark";
  const { get: getCompanySetting } = useCompanySettings();
  const [manualTicketCreationEnabled, setManualTicketCreationEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);

  // Exibir tickets conforme configuração da empresa (manualTicketCreation):
  //  • Se manualTicketCreation === 'enabled' (kanban avançado ON): mostrar SESSÕES (isOfficial === false)
  //  • Se manualTicketCreation !== 'enabled' (kanban avançado OFF): mostrar TODOS os tickets ATIVOS (open/pending)
  //  Sempre considerar apenas status abertos (open/pending) e evitar tickets sem status
  const visibleTickets = useMemo(() => {
    const wantSessions = manualTicketCreationEnabled === true;
    return tickets.filter(t => {
      if (!t) return false;
      // Tickets ativos: open, pending e group
      const statusOk = ["open", "pending", "group"].includes(t.status);
      // ON: filtra apenas sessões (isOfficial === false)
      // OFF: não filtra por isOfficial, mostra todos ativos
      return statusOk && (wantSessions ? t.isOfficial === false : true);
    });
  }, [tickets, manualTicketCreationEnabled]);

  // Função para buscar usuários e tickets
  const fetchUsersAndTickets = async () => {
    try {
      const [usersResponse, ticketsResponse] = await Promise.all([
        api.get("/users"),         // Endpoint que retorna todos os usuários
        api.get("/usersMoments"),    // Endpoint que retorna os tickets
      ]);
      // Garante que os dados sejam arrays
      const usersData = Array.isArray(usersResponse.data)
        ? usersResponse.data
        : (usersResponse.data.users || []);
      setUsers(usersData);
      setTickets(Array.isArray(ticketsResponse.data) ? ticketsResponse.data : []);
    } catch (err) {
      if (err.response?.status !== 500) {
        toastError(err);
      } else {
        toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
      }
    }
  };
  // Guardas para evitar tempestade de requisições
  const fetchInFlightRef = useRef(false);
  const disposedRef = useRef(false);
  const lastEventTsRef = useRef(0);

  // Efeito A: carregar configuração manualTicketCreation apenas quando companyId muda
  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const setting = await getCompanySetting({ column: "manualTicketCreation", noCache: true });
        if (disposed) return;
        setManualTicketCreationEnabled(setting?.manualTicketCreation === "enabled");
        setSettingsLoaded(true);
      } catch {
        if (!disposed) {
          setManualTicketCreationEnabled(false);
          setSettingsLoaded(true);
        }
      }
    })();
    return () => {
      disposed = true;
    };
  }, [companyId]);

  // Efeito B: fetch de dados + polling + listeners de socket
  useEffect(() => {
    disposedRef.current = false;

    const safeFetch = async () => {
      if (fetchInFlightRef.current || disposedRef.current) return;
      fetchInFlightRef.current = true;
      try {
        await fetchUsersAndTickets();
      } finally {
        fetchInFlightRef.current = false;
      }
    };

    // Busca inicial
    safeFetch();

    // Polling a cada 6,5s
    const interval = setInterval(safeFetch, 6500);

    // Listeners de socket com leve limitação de frequência
    const onAppMessage = (data) => {
      if (
        data?.action === "create" ||
        data?.action === "update" ||
        data?.action === "delete"
      ) {
        const now = Date.now();
        if (now - lastEventTsRef.current < 500) return; // throttle simples de 500ms
        lastEventTsRef.current = now;
        safeFetch();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      disposedRef.current = true;
      clearInterval(interval);
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, companyId]);

  // Mescla os usuários com os tickets correspondentes
  // Se houver algum ticket para o usuário, usamos os dados do ticket para atualizar (por exemplo, o status online)
  const usersWithTickets = useMemo(() => {
    return users.map((userItem) => {
      const matchingTickets = visibleTickets.filter(
        (ticket) => ticket.user && ticket.user.id === userItem.id
      );
      // Se houver ticket(s), atualiza os dados do usuário com os dados do ticket
      const updatedUser = matchingTickets.length > 0
        ? { ...userItem, ...matchingTickets[0].user }
        : userItem;
      return { user: updatedUser, userTickets: matchingTickets };
    });
  }, [users, visibleTickets]);

  // Tickets pendentes (sem usuário associado)
  // Pendentes: sessões sem usuário atribuído (userId indefinido/null)
  const pendingTickets = useMemo(() => {
    // Pendentes: tickets sem usuário atribuído, ainda ativos (status filtrado acima)
    return visibleTickets.filter((ticket) => !ticket.userId);
  }, [visibleTickets]);

  // Cálculo para limitar altura das listas de tickets
  const totalCards = usersWithTickets.length + 1; // +1 para pendentes
  const limitListHeight = totalCards > 4;
  // Aproximadamente: header (70px) + 8 tickets * 64px + padding
  // Ajuste: altura máxima para monitor fullhd (~5 cards: 5*64 = 320px) + header (70) + padding (32)
  const maxListHeight = 70 + (5 * 64) + 32;

  // Renderiza a seção de usuários com seus tickets
  const renderUsers = useMemo(() => {
    const isDark = theme.palette.type === 'dark' || theme.palette.mode === 'dark';
    return usersWithTickets.map((group) => (
      <Grid item key={group.user.id}>
        <Paper elevation={2} className={classes.card} style={{
          borderRadius: 18,
          margin: 0,
          padding: 0,
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[isDark ? 3 : 2],
          border: isDark ? `1px solid ${theme.palette.divider}` : undefined,
          height: limitListHeight ? maxListHeight : 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header do usuário */}
          <Box display="flex" alignItems="center" px={2} py={2} borderBottom={`1px solid ${isDark ? theme.palette.divider : theme.palette.grey[200]}`}
            className={classes.cardHeader}
          >
            <Box position="relative" mr={2}>
              <Avatar
                alt={group.user?.name || 'Sem nome'}
                src={group.user?.profileImage ? `${backendUrl}/public/company${companyId}/user/${group.user.profileImage}` : null}
                style={{
                  width: 44,
                  height: 44,
                  background: isDark ? theme.palette.background.default : theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  boxShadow: isDark ? '0 1px 4px #0002' : '0 1px 4px #0001'
                }}
              />
              <Box style={{ position: 'absolute', top: 0, right: 0, transform: 'translate(35%,-35%)' }}>
                <UserStatusIcon user={group.user} />
              </Box>
            </Box>
            <Typography variant="h6" style={{ flex: 1, fontWeight: 600, color: theme.palette.text.primary, letterSpacing: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {group.user?.name || 'Usuário'}
            </Typography>
            <Box display="flex" alignItems="center" ml={2}>
              <Forum style={{ color: theme.palette.primary.main, marginRight: 4, fontSize: 22 }} />
              <Typography style={{ color: theme.palette.primary.main, fontWeight: 700, fontSize: 18, minWidth: 24, textAlign: 'center' }}>
                {group.userTickets.length}
              </Typography>
            </Box>
          </Box>
          {/* Lista de tickets */}
          <Box
            style={limitListHeight ? { overflowY: 'auto', flex: 1, minHeight: 0 } : {}}
          >
            {group.userTickets.length > 0 ? group.userTickets.map((ticket) => (
              <Box
                key={ticket.id}
                display="flex"
                flexDirection="row"
                alignItems="flex-start"
                px={2}
                py={2}
                borderBottom={`1px solid ${isDark ? theme.palette.divider : theme.palette.grey[200]}`}
                className={isDark ? `${classes.ticketBox} ${classes.ticketBoxDark}` : classes.ticketBox}
              >
                <Avatar
                  alt={ticket?.contact?.name || 'Sem nome'}
                  src={ticket?.contact?.urlPicture}
                  style={{
                    width: 40,
                    height: 40,
                    marginRight: theme.spacing(2),
                    background: isDark ? theme.palette.background.default : theme.palette.grey[200],
                    color: theme.palette.text.primary
                  }}
                />
                <Box flex={1} minWidth={0}>
                  {/* Linha 1: nome, horário, badge, visualizar */}
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" style={{ fontWeight: 600, color: theme.palette.text.primary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15 }}>
                      {ticket?.contact?.name || 'Contato'}
                    </Typography>
                    <Box display="flex" alignItems="center" ml={1}>
                      <Typography style={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: 13, marginRight: theme.spacing(1) }}>
                        {ticket?.updatedAt && isSameDay(parseISO(ticket.updatedAt), new Date())
                          ? format(parseISO(ticket.updatedAt), 'HH:mm')
                          : ticket?.updatedAt
                            ? format(parseISO(ticket.updatedAt), 'dd/MM/yyyy')
                            : ''}
                      </Typography>
                      {ticket.unreadMessages > 0 && (
                        <Chip
                          label={ticket.unreadMessages}
                          size="small"
                          style={{ background: theme.palette.success.main, color: theme.palette.getContrastText(theme.palette.success.main), fontWeight: 700, fontSize: 12, borderRadius: 999, minWidth: 20, height: 18, marginRight: theme.spacing(1) }}
                        />
                      )}
                      <Tooltip title="Visualizar Ticket">
                        <VisibilityOutlined
                          fontSize="small"
                          style={{ color: theme.palette.action.active, cursor: 'pointer' }}
                          onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                  {/* Linha 2: chips de canal/fila/tags */}
                  <Box display="flex" alignItems="center" mt={0.5} mb={0.5} flexWrap="wrap" gap={8}>
                    {ticket.whatsapp && (
                      <Tooltip title={ticket.whatsapp.name}>
                        <Chip
                          label={ticket.whatsapp.name}
                          size="small"
                          style={{
                            background: theme.palette.info.main,
                            color: theme.palette.getContrastText(theme.palette.info.main),
                            fontWeight: 500,
                            fontSize: 11,
                            borderRadius: 8,
                            marginRight: theme.spacing(0.5),
                            height: 18
                          }}
                        />
                      </Tooltip>
                    )}
                    {ticket.queue && (
                      <Tooltip title={ticket.queue.name}>
                        <Chip
                          label={ticket.queue.name}
                          size="small"
                          style={{
                            background: ticket.queue.color || theme.palette.primary.main,
                            color: theme.palette.getContrastText(ticket.queue.color || theme.palette.primary.main),
                            fontWeight: 500,
                            fontSize: 11,
                            borderRadius: 8,
                            marginRight: theme.spacing(0.5),
                            height: 18
                          }}
                        />
                      </Tooltip>
                    )}
                    {ticket.tags && ticket.tags.length > 0 && ticket.tags.map((tag) => (
                      <Tooltip key={tag.id} title={tag.name}>
                        <Chip
                          label={tag.name}
                          size="small"
                          style={{
                            background: tag.color || theme.palette.action.selected,
                            color: theme.palette.getContrastText(tag.color || theme.palette.action.selected),
                            fontWeight: 500,
                            fontSize: 11,
                            borderRadius: 8,
                            marginRight: theme.spacing(0.5),
                            height: 18
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                  {/* Linha 3: mensagem */}
                  <Typography style={{ fontSize: 13, color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                    {ticket?.lastMessage || ''}
                  </Typography>
                </Box>
              </Box>
            )) : (
              <Typography variant="body2" style={{ padding: 10, color: theme.palette.text.secondary }}>
                Nenhum atendimento
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
    ));
  }, [usersWithTickets, classes, backendUrl, companyId, theme, history, limitListHeight, maxListHeight, isDark]);

  // Renderiza a seção de tickets pendentes
  const renderPending = useMemo(() => {
    const isDark = theme.palette.type === 'dark' || theme.palette.mode === 'dark';
    return (
      <Grid item>
        <Paper elevation={2} className={classes.card} style={{
          borderRadius: 18,
          margin: 0,
          padding: 0,
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[isDark ? 3 : 2],
          border: isDark ? `1px solid ${theme.palette.divider}` : undefined,
          height: limitListHeight ? maxListHeight : 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box display="flex" alignItems="center" px={2} py={2} borderBottom={`1px solid ${isDark ? theme.palette.divider : theme.palette.grey[200]}`}
            className={classes.cardHeaderPending}
          >
            <Avatar style={{ width: 44, height: 44, marginRight: theme.spacing(2), background: isDark ? theme.palette.background.default : theme.palette.grey[300], color: theme.palette.text.primary }} />
            <Typography variant="h6" style={{ flex: 1, fontWeight: 500, color: theme.palette.text.primary, letterSpacing: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Pendentes
            </Typography>
            <Box display="flex" alignItems="center" ml={2}>
              <Forum style={{ color: theme.palette.primary.main, marginRight: 4, fontSize: 22 }} />
              <Typography style={{ color: theme.palette.primary.main, fontWeight: 700, fontSize: 18, minWidth: 24, textAlign: 'center' }}>
                {pendingTickets.length}
              </Typography>
            </Box>
          </Box>
          <Box
            style={limitListHeight ? { overflowY: 'auto', flex: 1, minHeight: 0 } : {}}
          >
            {pendingTickets.length > 0 ? pendingTickets.map((ticket) => (
              <Box
                key={ticket.id}
                display="flex"
                flexDirection="row"
                alignItems="flex-start"
                px={2}
                py={2}
                borderBottom={`1px solid ${isDark ? theme.palette.divider : theme.palette.grey[200]}`}
                className={isDark ? `${classes.ticketBox} ${classes.ticketBoxDark}` : classes.ticketBox}
              >
                <Avatar
                  alt={ticket?.contact?.name || 'Sem nome'}
                  src={ticket?.contact?.urlPicture}
                  style={{
                    width: 40,
                    height: 40,
                    marginRight: theme.spacing(2),
                    background: isDark ? theme.palette.background.default : theme.palette.grey[200],
                    color: theme.palette.text.primary
                  }}
                />
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" style={{ fontWeight: 600, color: theme.palette.text.primary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15 }}>
                      {ticket?.contact?.name || 'Contato'}
                    </Typography>
                    <Box display="flex" alignItems="center" ml={1}>
                      <Typography style={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: 13, marginRight: theme.spacing(1) }}>
                        {ticket?.updatedAt && isSameDay(parseISO(ticket.updatedAt), new Date())
                          ? format(parseISO(ticket.updatedAt), 'HH:mm')
                          : ticket?.updatedAt
                            ? format(parseISO(ticket.updatedAt), 'dd/MM/yyyy')
                            : ''}
                      </Typography>
                      {ticket.unreadMessages > 0 && (
                        <Chip
                          label={ticket.unreadMessages}
                          size="small"
                          style={{ background: theme.palette.success.main, color: theme.palette.getContrastText(theme.palette.success.main), fontWeight: 700, fontSize: 12, borderRadius: 999, minWidth: 20, height: 18, marginRight: theme.spacing(1) }}
                        />
                      )}
                      <Tooltip title="Visualizar Ticket">
                        <VisibilityOutlined
                          fontSize="small"
                          style={{ color: theme.palette.action.active, cursor: 'pointer' }}
                          onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" mt={0.5} mb={0.5} flexWrap="wrap" gap={8}>
                    {ticket.whatsapp && (
                      <Tooltip title={ticket.whatsapp.name}>
                        <Chip
                          label={ticket.whatsapp.name}
                          size="small"
                          style={{
                            background: theme.palette.info.main,
                            color: theme.palette.getContrastText(theme.palette.info.main),
                            fontWeight: 500,
                            fontSize: 11,
                            borderRadius: 8,
                            marginRight: theme.spacing(0.5),
                            height: 18
                          }}
                        />
                      </Tooltip>
                    )}
                    {ticket.queue && (
                      <Tooltip title={ticket.queue.name}>
                        <Chip
                          label={ticket.queue.name}
                          size="small"
                          style={{
                            background: ticket.queue.color || theme.palette.primary.main,
                            color: theme.palette.getContrastText(ticket.queue.color || theme.palette.primary.main),
                            fontWeight: 500,
                            fontSize: 11,
                            borderRadius: 8,
                            marginRight: theme.spacing(0.5),
                            height: 18
                          }}
                        />
                      </Tooltip>
                    )}
                    {ticket.tags && ticket.tags.length > 0 && ticket.tags.map((tag) => (
                      <Tooltip key={tag.id} title={tag.name}>
                        <Chip
                          label={tag.name}
                          size="small"
                          style={{
                            background: tag.color || theme.palette.action.selected,
                            color: theme.palette.getContrastText(tag.color || theme.palette.action.selected),
                            fontWeight: 500,
                            fontSize: 11,
                            borderRadius: 8,
                            marginRight: theme.spacing(0.5),
                            height: 18
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                  <Typography style={{ fontSize: 13, color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                    {ticket?.lastMessage || ''}
                  </Typography>
                </Box>
              </Box>
            )) : (
              <Typography variant="body2" style={{ padding: 10, color: theme.palette.text.secondary }}>
                Nenhum atendimento
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
    );
  }, [pendingTickets, classes, theme, history, limitListHeight, maxListHeight, isDark]);

  return (
    <Fragment>
      <Grid container spacing={2}>
        {renderUsers}
        {renderPending}
      </Grid>
    </Fragment>
  );
};

export default DashboardManage;
